import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  AttributeMapping,
  type GenericAttributeMapping,
} from '../../../../components/AttributeMapping';
import type { RootState } from '../../../../services/serviceTypes';
import { useFetchAttributesDataQuery } from '../../data-store/copilotApi';
import useAddAccessAttributeMappingLogic from '../../hooks/addAccessAttributeMappingLogic';
import ErrorBanner from '../ErrorBanner/ErrorBanner';

interface AttributeMappingExpandedViewProps {
  entitlementIndex: number;
}

/**
 * AttributeMappingExpandedView - Exactly like AccessAttributeMapping but only AttributeMapping
 */
function AttributeMappingExpandedView({
  entitlementIndex,
}: AttributeMappingExpandedViewProps) {
  // Get API URL from parsedTemplates and stepFormData for initial mappings
  const { parsedTemplates, stepFormData } = useSelector(
    (state: RootState) => state.copilot
  );

  const {
    allValidationErrors,
    mandatoryMappings,
    handleMappingsChange,
    createDynamicConfig,
  } = useAddAccessAttributeMappingLogic();

  // Get addAccessTemplates from Redux (same as removeAccessLogic)
  const addAccessTemplates = useSelector(
    (state: RootState) => state.copilot.addAccessEntitlementTemplates || []
  );

  // Parse template to extract initial mappings (same function as removeAccessLogic)
  const parseTemplateToMappings = useCallback(
    (
      template: { httpParams?: string } | undefined
    ): GenericAttributeMapping[] => {
      if (!template?.httpParams) {
        return [];
      }

      try {
        const httpParams = JSON.parse(template.httpParams) as {
          data?: Record<string, unknown>;
        };
        const data = httpParams.data || {};
        const mappings: GenericAttributeMapping[] = [];

        Object.entries(data).forEach(([externalField, saviyntValue], index) => {
          if (
            typeof saviyntValue === 'string' &&
            saviyntValue.includes('${usersObj.')
          ) {
            const match = saviyntValue.match(/\$\{usersObj\.([^}]+)\}/);

            if (match) {
              mappings.push({
                id: `mapping-${index}`,
                saviyntField: `usersObj.${match[1]}`,
                externalAppField: externalField,
                saviyntValue: '',
                externalAppValue: '',
              });
            }
          }
        });

        return mappings;
      } catch (error) {
        return [];
      }
    },
    []
  );

  const getInitialMappings = useCallback(
    (index: number): GenericAttributeMapping[] => {
      const addAccessStepData = stepFormData[`AddAccess_${index}`] as
        | {
            mappings?: GenericAttributeMapping[];
          }
        | undefined;

      if (
        addAccessStepData?.mappings &&
        addAccessStepData.mappings.length > 0
      ) {
        return addAccessStepData.mappings;
      }

      // Fall back to template parsing (same as removeAccessLogic)
      const currentTemplate = addAccessTemplates[index] as
        | { name?: string; httpParams?: string }
        | undefined;

      return parseTemplateToMappings(currentTemplate);
    },
    [stepFormData, addAccessTemplates, parseTemplateToMappings]
  );

  const mappings = getInitialMappings(entitlementIndex);

  const validationError = allValidationErrors[entitlementIndex] || null;

  const stepData = (parsedTemplates.AddAccess as {
    apiURL?: string;
    domainObjects?: string[];
  }) || {
    apiURL: '',
    domainObjects: [],
  };

  const apiURL = stepData.apiURL ?? '';
  const domainObjects = stepData.domainObjects ?? [];

  const queryResult = useFetchAttributesDataQuery(
    {
      apiURL,
      domainObjects,
    },
    {
      skip: !apiURL || !domainObjects,
    }
  );

  const dynamicConfig = createDynamicConfig(
    queryResult.data as
      | { tableArributes?: Record<string, { name: string }[]> }
      | undefined
  );

  return (
    <>
      <AttributeMapping
        config={dynamicConfig}
        mandatoryMappings={mappings.length === 0 ? mandatoryMappings : []}
        initialMappings={mappings}
        onMappingsChange={(newMappings) => {
          handleMappingsChange(entitlementIndex, newMappings);
        }}
        showSaveButton={false}
      />

      {validationError && <ErrorBanner message={validationError} />}
    </>
  );
}

export default AttributeMappingExpandedView;
